import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, ArrowLeft, CheckCircle } from 'lucide-react';
import { productData } from '../data/productData';
import { useTranslation } from 'react-i18next';

const ProductDetail = () => {
  const { productId } = useParams();
  const product = productData[productId];
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('products.productNotFound')}</h1>
          <Link to="/products">
            <Button className="bg-[#4A5F8F] hover:bg-[#3A4F7F]">
              {t('products.backToProducts')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#4A5F8F] to-[#2C3E5F] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/products" className="inline-flex items-center text-white hover:text-gray-200 mb-6">
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('products.backToProducts')}
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <Badge className="mb-4 bg-white text-[#4A5F8F] hover:bg-gray-100">
                {product.standard}
              </Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                {currentLang === 'en' ? product.name : t(`productData.${product.id}.name`)}
              </h1>
              <p className="text-lg md:text-xl text-gray-100">
                {currentLang === 'en' ? product.subtitle : t(`productData.${product.id}.subtitle`)}
              </p>
            </div>
            {product.catalogUrl && (
              <a href={product.catalogUrl} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                <Button size="lg" className="bg-white text-[#4A5F8F] hover:bg-gray-100 w-full sm:w-auto">
                  <Download className="mr-2 h-5 w-5" />
                  {currentLang === 'en' ? 'Download Catalogue' : 'ಕ್ಯಾಟಲಾಗ್ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ'}
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {/* Product Image */}
            <div className="lg:col-span-1">
              <Card className="overflow-hidden">
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="w-full h-[400px] object-cover"
                />
              </Card>
            </div>

            {/* Quick Info */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {currentLang === 'en' ? 'Product Overview' : 'ಉತ್ಪನ್ನ ಅವಲೋಕನ'}
                  </h2>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    {currentLang === 'en' ? product.description : t(`productData.${product.id}.description`)}
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600 mb-1">
                        {currentLang === 'en' ? 'Standard' : 'ಮಾನದಂಡ'}
                      </div>
                      <div className="text-lg font-semibold text-[#4A5F8F]">{product.standard}</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600 mb-1">
                        {currentLang === 'en' ? 'Size Range' : 'ಗಾತ್ರ ಶ್ರೇಣಿ'}
                      </div>
                      <div className="text-lg font-semibold text-[#4A5F8F]">
                        {currentLang === 'en' ? product.range : t(`productData.${product.id}.range`)}
                      </div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-600 mb-1">
                        {currentLang === 'en' ? 'Material' : 'ವಸ್ತು'}
                      </div>
                      <div className="text-lg font-semibold text-[#4A5F8F]">UPVC</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      {currentLang === 'en' ? 'Key Features:' : 'ಪ್ರಮುಖ ವೈಶಿಷ್ಟ್ಯಗಳು:'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(currentLang === 'en' ? product.features : t(`productData.${product.id}.features`, { returnObjects: true })).map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-[#4A5F8F] mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Detailed Tabs - All options clearly visible */}
          <Tabs defaultValue="specifications" className="w-full">
            <TabsList className="w-full h-auto flex flex-col sm:grid sm:grid-cols-3 gap-2 sm:gap-1 p-2 bg-transparent">
              <TabsTrigger 
                value="specifications" 
                className="w-full text-xs sm:text-sm px-3 py-3 border-2 border-[#4A5F8F] rounded-lg data-[state=active]:bg-[#4A5F8F] data-[state=active]:text-white data-[state=inactive]:bg-white data-[state=inactive]:text-[#4A5F8F] font-semibold shadow-sm hover:shadow-md transition-all"
              >
                {currentLang === 'en' ? 'Technical Specifications' : 'ತಾಂತ್ರಿಕ ವಿಶೇಷಣಗಳು'}
              </TabsTrigger>
              <TabsTrigger 
                value="applications" 
                className="w-full text-xs sm:text-sm px-3 py-3 border-2 border-[#4A5F8F] rounded-lg data-[state=active]:bg-[#4A5F8F] data-[state=active]:text-white data-[state=inactive]:bg-white data-[state=inactive]:text-[#4A5F8F] font-semibold shadow-sm hover:shadow-md transition-all"
              >
                {currentLang === 'en' ? 'Applications' : 'ಅನ್ವಯಗಳು'}
              </TabsTrigger>
              <TabsTrigger 
                value="advantages" 
                className="w-full text-xs sm:text-sm px-3 py-3 border-2 border-[#4A5F8F] rounded-lg data-[state=active]:bg-[#4A5F8F] data-[state=active]:text-white data-[state=inactive]:bg-white data-[state=inactive]:text-[#4A5F8F] font-semibold shadow-sm hover:shadow-md transition-all"
              >
                {currentLang === 'en' ? 'Advantages' : 'ಪ್ರಯೋಜನಗಳು'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="specifications" className="mt-6">
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {currentLang === 'en' ? 'Technical Specifications' : 'ತಾಂತ್ರಿಕ ವಿಶೇಷಣಗಳು'}
                  </h2>
                  {product.specifications && (
                    <div className="space-y-6">
                      {product.specifications.text && (
                        <p className="text-gray-700 leading-relaxed">
                          {currentLang === 'en' ? product.specifications.text : t(`productData.${product.id}.specifications.text`)}
                        </p>
                      )}
                      
                      {product.specifications.table && (
                        <div className="overflow-x-auto">
                          <table className="min-w-full border-collapse border border-gray-300">
                            <thead>
                              <tr className="bg-[#4A5F8F] text-white">
                                {(currentLang === 'en' ? product.specifications.table.headers : t(`productData.${product.id}.specifications.headers`, { returnObjects: true })).map((header, idx) => (
                                  <th key={idx} className="border border-gray-300 px-4 py-3 text-left">
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {product.specifications.table.rows.map((row, idx) => (
                                <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                  {row.map((cell, cellIdx) => (
                                    <td key={cellIdx} className="border border-gray-300 px-4 py-2">
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {product.specifications.additionalInfo && (
                        <div className="mt-6 space-y-2">
                          {(currentLang === 'en' ? product.specifications.additionalInfo : t(`productData.${product.id}.specifications.additionalInfo`, { returnObjects: true })).map((info, idx) => (
                            <p key={idx} className="text-gray-700">• {info}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="applications" className="mt-6">
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {currentLang === 'en' ? 'Applications' : 'ಅನ್ವಯಗಳು'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(currentLang === 'en' ? product.applications : t(`productData.${product.id}.applications`, { returnObjects: true })).map((app, idx) => (
                      <div key={idx} className="flex items-start">
                        <div className="w-8 h-8 bg-[#4A5F8F] bg-opacity-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                          <CheckCircle className="w-5 h-5 text-[#4A5F8F]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">{app.title}</h3>
                          <p className="text-gray-600">{app.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="advantages" className="mt-6">
              <Card>
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {currentLang === 'en' ? 'Product Advantages' : 'ಉತ್ಪನ್ನ ಪ್ರಯೋಜನಗಳು'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(currentLang === 'en' ? product.advantages : t(`productData.${product.id}.advantages`, { returnObjects: true })).map((advantage, idx) => (
                      <div key={idx} className="flex items-start">
                        <div className="w-8 h-8 bg-[#4A5F8F] bg-opacity-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                          <CheckCircle className="w-5 h-5 text-[#4A5F8F]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">{advantage.title}</h3>
                          <p className="text-gray-600">{advantage.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* CTA Section */}
          <Card className="mt-12 bg-gradient-to-br from-[#4A5F8F] to-[#2C3E5F] text-white border-none">
            <CardContent className="p-8 text-center">
              <h2 className="text-3xl font-bold mb-4">
                {currentLang === 'en' ? 'Need More Information?' : 'ಹೆಚ್ಚಿನ ಮಾಹಿತಿ ಬೇಕೇ?'}
              </h2>
              <p className="text-xl mb-6 text-gray-100">
                {currentLang === 'en' ? 'Contact us for detailed specifications, pricing, or bulk orders' : 'ವಿವರವಾದ ವಿಶೇಷಣಗಳು, ಬೆಲೆ ಅಥವಾ ಬೃಹತ್ ಆದೇಶಗಳಿಗಾಗಿ ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ'}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-[#4A5F8F] hover:bg-gray-100">
                    {currentLang === 'en' ? 'Contact Us' : 'ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ'}
                  </Button>
                </Link>
                {product.catalogUrl && (
                  <a href={product.catalogUrl} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#4A5F8F]">
                      <Download className="mr-2 h-5 w-5" />
                      {currentLang === 'en' ? 'Download Catalogue' : 'ಕ್ಯಾಟಲಾಗ್ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ'}
                    </Button>
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
